/**
 * App-level error boundary. Catches uncaught render/runtime errors anywhere in the
 * component tree and shows a friendly recovery screen instead of a blank white page.
 *
 * Class component because React error boundaries must use getDerivedStateFromError /
 * componentDidCatch (no hook equivalent). Navigation in the fallback uses plain links so
 * it works without router context.
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to the console (and any console-forwarding) for debugging.
    // eslint-disable-next-line no-console
    console.error('Uncaught UI error:', error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-black/[0.06] shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#c41e3a]/10 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-[#c41e3a]" />
          </div>
          <h1 className="text-xl font-bold text-[#0f172a] mb-2">Something went wrong</h1>
          <p className="text-sm text-[#475569] mb-6">
            An unexpected error interrupted the page. Reloading usually fixes it. If it keeps
            happening, contact support at{' '}
            <a href="mailto:contact@autoaccesssolutions.org" className="text-[#c41e3a] hover:underline">
              contact@autoaccesssolutions.org
            </a>.
          </p>

          {isDev && this.state.error && (
            <pre className="text-left text-xs text-[#dc2626] bg-[#fef2f2] border border-[#fecaca] rounded-lg p-3 mb-6 overflow-auto max-h-40 whitespace-pre-wrap">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#a01830] text-white text-sm font-semibold hover:from-[#a01830] hover:to-[#8a1428] transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Reload page
            </button>
            <a
              href="/dashboard"
              className="px-5 py-2.5 rounded-lg border border-black/[0.08] text-[#475569] text-sm font-medium hover:border-[#c41e3a]/40 transition-all"
            >
              Go to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
